import { Component } from 'react'

import find from 'lodash/find'
import PropTypes from 'prop-types'
import qs from 'qs'

import { fromGlobalId } from 'utils/transformations/graphql'

import { ALL_COLLECTIONS } from 'shop/formatters'
import { NONE_GROUP_BY_TAG, NONE_SORT_BY_TAG } from 'store/shop/constants'

import Modal from 'components/Core/Modal'
import { PRODUCT_CATALOG } from 'routes/paths'

import { BrandStartOrderIds } from '../StartOrderModal.ids'
import styles from '../StartOrderModal.module.css'
import {
  NEW_CUSTOMER_KEY,
  getCollectionOptions,
  getOrderEventOptions,
  getOrderTypeOptions,
  getPriceTypeOptions,
  getRetailerOptions,
  getWarehouseOptions,
} from './BrandStartOrderModal.formatters'
import messages from './BrandStartOrderModal.messages'
import { MINIMUM_CHARACTERS_QUERY } from './BrandStartOrderModal.queries'
import { hasValidCustomerSelection } from './BrandStartOrderModal.utils'
import { CollectionDropdown } from './components/CollectionDropdown/CollectionDropdown'
import { CustomerEmail } from './components/CustomerEmail/CustomerEmail'
import { DoorsDropdown } from './components/DoorsDropdown/DoorsDropdown'
import { EventDropdown } from './components/EventDropdown/EventDropdown'
import { MessageBox } from './components/MessageBox/MessageBox'
import { OrderTypeDropdown } from './components/OrderTypeDropdown/OrderTypeDropdown'
import { PriceTypeDropdown } from './components/PriceTypeDropdown/PriceTypeDropdown'
import { RetailerDropdown } from './components/RetailerDropdown/RetailerDropdown'
import { WarehouseDropdown } from './components/WarehouseDropdown/WarehouseDropdown'
import { COLLECTIONS_KEY } from 'containers/ProductCatalog/CatalogFilters/filters'

export default class BrandStartOrderModal extends Component {
  state = {
    selectedDoorIds: this?.props?.preselectedDoors || [],
    selectedCollectionId: this.props.preselectedCollectionId || ALL_COLLECTIONS,
    selectedOrderEventId: this.props.preselectedEvent?.id || null,
    retailerOptions: this.props.preselectedRetailer?.id
      ? [
          {
            options: [
              {
                id: this.props.preselectedRetailer.id,
                value: this.props.preselectedRetailer.id,
                label: this.props.preselectedRetailer.name,
              },
            ],
            label: messages.connected,
          },
        ]
      : [],
    selectedPriceTypeId: this.props?.preselectedPriceType?.id || null,
    selectedOrderTypeId: this.props.preselectedOrderTypeId || null,
    selectedWarehouseId: this?.props?.preselectedWarehouse?.id || null,
    selectedRetailerId: this.props?.preselectedRetailer?.id || null,
    isAddingNewCustomer: false,
    selectedOrphanRetailerId: null,
  }

  componentDidUpdate(prevProps) {
    const {
      connectedAccountsQueryLoading,
      orphanRetailersQueryLoading,
      retailerSearchText,
      dataForRetailerQueryLoading,
      orderTypes,
      preselectedPriceType,
      preselectedWarehouse,
      doors,
    } = this.props
    const preselectedData = Boolean(
      preselectedPriceType?.id || preselectedWarehouse?.id,
    )

    const { selectedOrderTypeId } = this.state

    const finishedLoading =
      (prevProps.connectedAccountsQueryLoading &&
        !connectedAccountsQueryLoading) ||
      (prevProps.orphanRetailersQueryLoading && !orphanRetailersQueryLoading)

    const searchTextChanged =
      prevProps.retailerSearchText !== retailerSearchText
    const minimumCharactersReached =
      retailerSearchText.length < MINIMUM_CHARACTERS_QUERY
    const finishedRetailLoading =
      prevProps.dataForRetailerQueryLoading && !dataForRetailerQueryLoading
    if (finishedLoading || searchTextChanged) {
      const retailerOptions = minimumCharactersReached
        ? []
        : getRetailerOptions({
            retailerSearchText,
            isAddClientRestricted: this.props.isAddClientRestricted,
            quickStartOrderBetaFlag: this.props.flags?.quickStartOrderBeta,
            connectedAccountsQueryLoading,
            orphanRetailersQueryLoading,
            connectedAccounts: this.props.connectedAccounts,
            orphanRetailers: this.props.orphanRetailers,
          })
      this.setState({
        retailerOptions,
      })
    }

    if (finishedRetailLoading && !preselectedData) {
      this.setWarehouseId()
      this.setPriceTypeId()

      if (doors.length === 1) {
        this.setState({ selectedDoorIds: [this.props.doors[0].value] })
      }
    }
    if (!selectedOrderTypeId && orderTypes.length > 0) {
      this.setState({ selectedOrderTypeId: orderTypes[0].id })
    }
  }

  componentWillUnmount() {
    const {
      changeRetailerSearchText,
      changeCollectionSearchText,
      setRetailerId,
      setOrphanRetailer,
    } = this.props

    setRetailerId()
    setOrphanRetailer()
    changeRetailerSearchText('')
    changeCollectionSearchText('')
  }

  setPriceTypeId = () => {
    const { priceTypes } = this.props
    let priceTypeMatches = priceTypes
    return priceTypeMatches.length
      ? this.setState({ selectedPriceTypeId: priceTypeMatches[0].id })
      : this.setState({ selectedPriceTypeId: null })
  }

  setWarehouseId = () => {
    const { warehouses } = this.props
    let warehouseMatches = warehouses

    if (warehouseMatches.length > 0) {
      const defaultWarehouse = find(warehouseMatches, 'default')
      this.setState({
        selectedWarehouseId: defaultWarehouse
          ? defaultWarehouse.id
          : warehouseMatches[0].id,
      })
    } else {
      this.setState({ selectedWarehouseId: null })
    }
  }

  setSelectedWarehouse = () => {
    const { setWarehouse, warehouses } = this.props
    let warehouseMatches = warehouses
    const { selectedWarehouseId } = this.state
    const selectedWarehouse = warehouseMatches.find(
      (warehouse) => warehouse.id === selectedWarehouseId,
    )
    setWarehouse(selectedWarehouse)
  }

  setSelectedEvent = () => {
    const { orderEvents, setEvent } = this.props
    const { selectedOrderEventId } = this.state
    const orderEvent = orderEvents.find(
      (event) => event.id === selectedOrderEventId,
    )
    setEvent(orderEvent)
  }

  setSelectedDoors = () => {
    const { doors, setDoors } = this.props
    const { selectedDoorIds } = this.state
    let doorMatches = doors
    const selectedDoors = selectedDoorIds.map((selectDoorId) =>
      doorMatches.find((door) => door.value === selectDoorId),
    )
    setDoors(selectedDoors)
  }

  setPriceTypeData = () => {
    const { priceTypes, setPriceTypes, setOrderPriceType } = this.props
    const { selectedPriceTypeId } = this.state
    let priceTypeMatches = priceTypes
    const selected =
      priceTypeMatches.length === 1
        ? priceTypeMatches[0]
        : priceTypeMatches.find(
            (priceType) => priceType.id === selectedPriceTypeId,
          )
    setPriceTypes(priceTypeMatches)
    setOrderPriceType(selected)
  }

  /**
   * After adding support for preselectedRetailer this method must
   * be aware of where is it getting the selectedRetailer from.
   * In case we're passing a preselectedRetailer, connectedAccounts
   * props will be expected to be empty so we need to build an array
   * with out preselectedRetailer.
   *
   * There are also some cases where retailerId might be empty after opening
   * the modal with a preselectedRetaielr, so we also solve for that case.
   */
  getSelectedRetailer = (retailerId) => {
    const isRetailerPreselected = Boolean(this.props.preselectedRetailer?.id)
    const searchChanged = Boolean(this.props.retailerSearchText)

    const retailerOptions =
      isRetailerPreselected && !searchChanged
        ? [
            {
              ...this.props.preselectedRetailer,
              name: this.props.preselectedRetailer?.name,
              text: this.props.preselectedRetailer?.name,
            },
          ]
        : this.props.connectedAccounts?.map((ro) => ({
            ...ro,
            name: ro?.displayName,
          }))

    if (!retailerId && isRetailerPreselected) {
      retailerId = this.props.preselectedRetailer?.id
    }

    const foundRetailer = find(
      retailerOptions,
      (account) => account.id === retailerId,
    )

    return foundRetailer
  }

  getRequestData = () => {
    const { connectedAccounts, orderEvents, doors, warehouses } = this.props
    const {
      selectedDoorIds,
      selectedOrderEventId,
      selectedWarehouseId,
      selectedPriceTypeId,
      selectedOrderTypeId,
      selectedRetailerId,
    } = this.state
    const selectedRetailer = connectedAccounts.find(
      (connectedAccount) => connectedAccount.id === selectedRetailerId,
    )
    const { id: retailerId } = fromGlobalId(selectedRetailer.id)
    const doorIds = selectedDoorIds.map((selectedDoorId) => {
      const { id: doorId } = fromGlobalId(selectedDoorId)
      return doorId
    })

    const doorNames = selectedDoorIds.map((selectDoorId) =>
      doors.find((door) => door.value === selectDoorId),
    )
    this.setSelectedDoors()
    const orderEvent = orderEvents.find(
      (event) => event.id === selectedOrderEventId,
    )

    const { id: priceTypeId } = fromGlobalId(selectedPriceTypeId)

    const orderWarehouse = warehouses.find(
      (warehouse) => warehouse.id === selectedWarehouseId,
    )

    const { id: orderTypeId } = fromGlobalId(selectedOrderTypeId)
    const data = {
      retailer_name: selectedRetailer.displayName,
      retailer_id: retailerId,
      door_ids: doorIds,
      door_names: doorNames,
      event_name: orderEvent && orderEvent.name,
      warehouse: orderWarehouse,
      price_type: priceTypeId,
      order_type_id: orderTypeId,
    }
    return qs.stringify(data)
  }

  setSelectedDoorIds = (selectedDoorIds) => {
    this.setState({ selectedDoorIds })
  }

  setSelectedOrderTypeId = (selectedOrderTypeId) => {
    this.setState({ selectedOrderTypeId })
  }

  setSelectedCollectionId = (selectedCollectionId) => {
    this.setState({ selectedCollectionId })
  }

  getSelectedCollectionId = () => {
    const { selectedCollectionId } = this.state
    const isListed = find(
      this.props.collections,
      (collection) => collection.key === selectedCollectionId,
    )
    return isListed ? selectedCollectionId : ALL_COLLECTIONS
  }

  setSelectedOrderEventId = (selectedOrderEventId) => {
    this.setState({ selectedOrderEventId })
  }

  setSelectedPriceTypeId = (selectedPriceTypeId) => {
    this.setState({ selectedPriceTypeId })
  }

  setSelectedWarehouseId = (selectedWarehouseId) => {
    this.setState({ selectedWarehouseId })
  }

  setRetailerSelection = (selection) => {
    this.setState(selection)
  }

  deselectAllFilters = () => {
    const {
      toggleDeselectAllFilters,
      applySelectedFilters,
      setSearchText,
      setGroupByTagInfo,
      setSortByTagInfo,
    } = this.props

    toggleDeselectAllFilters()
    applySelectedFilters()
    setSearchText('')
    setGroupByTagInfo(NONE_GROUP_BY_TAG)
    setSortByTagInfo(NONE_SORT_BY_TAG)
  }

  createOrder = () => {
    const selectedCollectionId = this.getSelectedCollectionId()
    const {
      selectedOrderEventId,
      retailerOptions,
      selectedRetailerId,
      selectedOrderTypeId,
    } = this.state

    const {
      setPageLoaderActive,
      trackStartOrderModalSubmitted,
      navigate,
      collections,
      setFilter,
      setRetailer,
      setOrderTypeId,
      setShopOrphanRetailer,
      orphanRetailer,
      orderEvents,
    } = this.props

    const eventsOptions = getOrderEventOptions(orderEvents)
    this.deselectAllFilters()
    setPageLoaderActive(true)

    trackStartOrderModalSubmitted({
      retailerOptions,
      collectionOptions: collections,
      selectedCollectionId,
      eventsOptions,
      selectedOrderEventId,
    })

    if (selectedCollectionId === ALL_COLLECTIONS) {
      setFilter({ key: COLLECTIONS_KEY })
    } else {
      const selectedCollection = find(
        collections,
        (collection) => collection.key === selectedCollectionId,
      )
      setFilter({ key: COLLECTIONS_KEY, value: selectedCollection })
    }
    const selectedRetailer = this.getSelectedRetailer(selectedRetailerId)
    setShopOrphanRetailer(orphanRetailer)
    setRetailer(selectedRetailer)
    setOrderTypeId(selectedOrderTypeId)

    this.setSelectedDoors()
    this.setSelectedWarehouse()
    this.setSelectedEvent()
    this.setPriceTypeData()
    navigate(PRODUCT_CATALOG)
    return null
  }

  customOnPrimaryButtonClick = () => {
    const {
      selectedRetailerId,
      selectedWarehouseId,
      selectedOrderEventId,
      selectedOrderTypeId,
      selectedPriceTypeId,
      selectedDoorIds,
    } = this.state
    const { onPrimaryButtonClick, doors, warehouses, orderEvents, priceTypes } =
      this.props

    const defaultPriceType = priceTypes?.[0]
    const priceType = selectedPriceTypeId
      ? priceTypes.find((priceType) => priceType.id === selectedPriceTypeId)
      : defaultPriceType

    const primaryClickProps = {
      retailer: this.getSelectedRetailer(selectedRetailerId),
      doors: doors?.filter((door) => selectedDoorIds.includes(door.key)),
      warehouse: warehouses?.find(
        (warehouse) => warehouse.id === selectedWarehouseId,
      ),
      event: orderEvents?.find((event) => event.id === selectedOrderEventId),
      orderTypeId: selectedOrderTypeId,
      priceType,
    }
    onPrimaryButtonClick(primaryClickProps)
  }

  redirectUserToShop = () => {
    const selectedCollectionId = this.getSelectedCollectionId()
    const { navigate, accountId } = this.props
    if (selectedCollectionId === ALL_COLLECTIONS) {
      navigate(`/collections/view/?brandId=${accountId}`)
    } else {
      const { id: collectionId } = fromGlobalId(selectedCollectionId)
      navigate(`/collections/view/${collectionId}`)
    }
  }

  handleClose = () => {
    this.props.trackStartOrderModalCancelled()
    this.props.onClose()
  }

  render() {
    const {
      dataForRetailerQueryLoading,
      doors,
      initialStartOrderQueryLoading,
      orderEvents,
      orderTypes,
      connectedAccountsQueryLoading,
      orphanRetailersQueryLoading,
      forceComplete,
      collections,
      collectionsLoading,
      modalTitle,
      priceTypes,
      selectCollection,
      onPrimaryButtonClick,
      alwaysShowPriceType,
      warehouses,
      setOrphanRetailer,
      orphanRetailer,
      orphanRetailers,
      setRetailerId,
      changeRetailerSearchText,
      changeCollectionSearchText,
      retailerSearchText,
      isAddClientRestricted,
      flags,
    } = this.props
    const {
      selectedDoorIds,
      selectedOrderEventId,
      retailerOptions,
      selectedPriceTypeId,
      selectedOrderTypeId,
      selectedWarehouseId,
      selectedRetailerId,
      isAddingNewCustomer,
      selectedOrphanRetailerId,
    } = this.state
    const shouldHideMessageBox =
      isAddClientRestricted ||
      !flags?.quickStartOrderBeta ||
      selectedRetailerId ||
      selectedOrphanRetailerId ||
      isAddingNewCustomer
    let [priceTypeMatches, doorMatches, warehouseMatches] = [
      priceTypes,
      doors,
      warehouses,
    ]
    const validCustomerNameSelected = hasValidCustomerSelection({
      isAddingNewCustomer,
      selectedRetailerId,
      selectedOrphanRetailerId,
      orphanRetailer,
    })
    const validCustomerEmailSelected = hasValidCustomerSelection({
      isAddingNewCustomer,
      selectedRetailerId,
      selectedOrphanRetailerId,
      orphanRetailer,
      checkForEmail: true,
    })
    const primaryDisabled =
      !validCustomerEmailSelected || !priceTypeMatches.length
    const showPriceTypeDropdown =
      alwaysShowPriceType || priceTypeMatches.length > 1
    return (
      <Modal
        id={BrandStartOrderIds.ModalTitle}
        title={modalTitle || messages.startOrder}
        primaryActionLabel={messages.startOrder}
        primaryActionOnClick={() => {
          if (onPrimaryButtonClick) {
            this.deselectAllFilters()
            this.customOnPrimaryButtonClick()
          } else {
            this.createOrder()
          }
        }}
        primaryDisabled={primaryDisabled}
        open
        onClose={this.handleClose}
        className={styles.StartOrder}
        loading={dataForRetailerQueryLoading || initialStartOrderQueryLoading}
        secondaryActionHidden={forceComplete}
        hideCloseButton={forceComplete}
        primaryActionId={
          isAddingNewCustomer
            ? BrandStartOrderIds.AcceptUnconnectedButton
            : BrandStartOrderIds.AcceptButton
        }
        secondaryActionId={BrandStartOrderIds.CancelButton}
        idCloseButton={BrandStartOrderIds.CloseButton}
      >
        {!shouldHideMessageBox && <MessageBox />}
        <RetailerDropdown
          loading={connectedAccountsQueryLoading || orphanRetailersQueryLoading}
          loadingDataForRetailer={
            dataForRetailerQueryLoading || initialStartOrderQueryLoading
          }
          options={retailerOptions}
          retailerSearchText={retailerSearchText}
          value={
            isAddingNewCustomer
              ? `${NEW_CUSTOMER_KEY}${retailerSearchText}`
              : selectedRetailerId || selectedOrphanRetailerId
          }
          selectedRetailerId={selectedRetailerId}
          validCustomerNameSelected={validCustomerNameSelected}
          hasPriceTypeMatches={priceTypeMatches.length > 0}
          orphanRetailers={orphanRetailers}
          setRetailerSelection={this.setRetailerSelection}
          setRetailerId={setRetailerId}
          setOrphanRetailer={setOrphanRetailer}
          changeRetailerSearchText={changeRetailerSearchText}
          changeCollectionSearchText={changeCollectionSearchText}
          isAddCostumerRestricted={
            isAddClientRestricted || !flags?.quickStartOrderBeta
          }
        />

        {(isAddingNewCustomer || selectedOrphanRetailerId) &&
          orphanRetailer && (
            <CustomerEmail
              orphanRetailer={orphanRetailer}
              setOrphanRetailer={setOrphanRetailer}
            />
          )}
        {doorMatches.length > 0 && validCustomerNameSelected && (
          <DoorsDropdown
            options={doorMatches}
            value={selectedDoorIds}
            setSelectedDoorIds={this.setSelectedDoorIds}
          />
        )}

        {showPriceTypeDropdown && validCustomerNameSelected && (
          <PriceTypeDropdown
            options={getPriceTypeOptions(priceTypeMatches)}
            value={selectedPriceTypeId}
            setSelectedPriceTypeId={this.setSelectedPriceTypeId}
          />
        )}

        {warehouseMatches.length >= 1 && validCustomerNameSelected && (
          <WarehouseDropdown
            options={getWarehouseOptions(warehouseMatches)}
            value={selectedWarehouseId}
            setSelectedWarehouseId={this.setSelectedWarehouseId}
          />
        )}
        {orderTypes.length > 0 && validCustomerNameSelected && (
          <OrderTypeDropdown
            options={getOrderTypeOptions(orderTypes)}
            value={selectedOrderTypeId}
            setSelectedOrderTypeId={this.setSelectedOrderTypeId}
          />
        )}
        {selectCollection && validCustomerNameSelected && (
          <CollectionDropdown
            loading={collectionsLoading}
            options={getCollectionOptions(collections)}
            value={this.getSelectedCollectionId()}
            setSelectedCollectionId={this.setSelectedCollectionId}
            changeCollectionSearchText={changeCollectionSearchText}
          />
        )}

        {validCustomerNameSelected && (
          <EventDropdown
            options={getOrderEventOptions(orderEvents)}
            value={selectedOrderEventId}
            setSelectedOrderEventId={this.setSelectedOrderEventId}
          />
        )}
      </Modal>
    )
  }
}

BrandStartOrderModal.propTypes = {
  accountId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  selectCollection: PropTypes.bool,
  setPageLoaderActive: PropTypes.func.isRequired,
  setRetailerId: PropTypes.func.isRequired,
  setRetailer: PropTypes.func.isRequired,
  setDoors: PropTypes.func.isRequired,
  setPriceTypes: PropTypes.func.isRequired,
  setOrderPriceType: PropTypes.func.isRequired,
  setWarehouse: PropTypes.func.isRequired,
  setEvent: PropTypes.func.isRequired,
  toggleDeselectAllFilters: PropTypes.func.isRequired,
  applySelectedFilters: PropTypes.func.isRequired,
  setSearchText: PropTypes.func.isRequired,
  setGroupByTagInfo: PropTypes.func.isRequired,
  setSortByTagInfo: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  setOrderTypeId: PropTypes.func.isRequired,
  preselectedRetailer: PropTypes.object,
  preselectedCollectionId: PropTypes.string,
  preselectedPriceType: PropTypes.object,
  preselectedWarehouse: PropTypes.object,
  preselectedEvent: PropTypes.object,
  preselectedOrderTypeId: PropTypes.string,
  preselectedDoor: PropTypes.arrayOf(PropTypes.string),
  alwaysShowPriceType: PropTypes.bool,
  connectedAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.string,
      locations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
        }),
      ),
    }),
  ),
  collections: PropTypes.array.isRequired,
  orderEvents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      code: PropTypes.string,
    }),
  ).isRequired,
  doors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  warehouses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  orderTypes: PropTypes.array.isRequired,
  priceTypes: PropTypes.array,
  initialStartOrderQueryLoading: PropTypes.bool,
  dataForRetailerQueryLoading: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
  changeRetailerSearchText: PropTypes.func.isRequired,
  connectedAccountsQueryLoading: PropTypes.bool,
  orphanRetailersQueryLoading: PropTypes.bool,
  retailerSearchText: PropTypes.string.isRequired,
  trackStartOrderModalSubmitted: PropTypes.func,
  trackStartOrderModalCancelled: PropTypes.func,
  flags: PropTypes.object,
  forceComplete: PropTypes.bool,
  changeCollectionSearchText: PropTypes.func,
  collectionsLoading: PropTypes.bool,
  modalTitle: PropTypes.string,
  onPrimaryButtonClick: PropTypes.func,
}

BrandStartOrderModal.defaultProps = {
  initialStartOrderQueryLoading: false,
  dataForRetailerQueryLoading: false,
  connectedAccountsQueryLoading: false,
  orphanRetailersQueryLoading: false,
  connectedAccounts: [],
  doors: [],
  priceTypes: [],
  warehouses: [],
  trackStartOrderModalSubmitted: () => {},
  trackStartOrderModalCancelled: () => {},
  flags: {},
  forceComplete: false,
  changeCollectionSearchText: () => {},
  collectionsLoading: false,
  selectCollection: true,
}
